/**
 * Numbers, money and dates, by the locale's own declared rules.
 *
 * Deliberately not Intl.NumberFormat. The platform records a decimal
 * separator, a thousand separator and a numeral system per locale in
 * config.locale, and a currency's minor units and display places in
 * config.currency, and those records are what the server formats from. If this
 * front end asked the browser instead, the same figure would be punctuated one
 * way on a page rendered by Python and another way on a page rendered here,
 * depending on the viewer's operating system. The configuration is the
 * authority; this file is a second implementation of it, and the smoke suite
 * compares the two outputs for exactly that reason.
 *
 * Nothing here computes a financial figure. Every amount arrives already
 * computed by SQL or by an engine. These functions punctuate.
 */
import type { Currency, Locale } from '../api/types'

const ARABIC_INDIC = ['٠', '١', '٢', '٣', '٤', '٥', '٦', '٧', '٨', '٩']
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
                'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
const MONTHS_AR = ['يناير', 'فبراير', 'مارس', 'أبريل', 'مايو', 'يونيو',
                   'يوليو', 'أغسطس', 'سبتمبر', 'أكتوبر', 'نوفمبر', 'ديسمبر']

export const DASH = '—'

function digits(text: string, locale: Locale) {
  if (locale.numeral_system !== 'arab') return text
  return text.replace(/[0-9]/g, (d) => ARABIC_INDIC[Number(d)])
}

/** Half up, like the server's ROUND_HALF_UP, not the half-even that
 *  toFixed gives on some values. A rounding rule that differs between the two
 *  tiers is a figure that differs between the two tiers. */
function roundHalfUp(value: number, places: number) {
  const factor = 10 ** places
  const scaled = value * factor
  // The epsilon corrects the binary representation of values such as 1.005,
  // which is stored slightly below its decimal form and would otherwise round
  // down where the server rounds up.
  const corrected = Math.round(Math.abs(scaled) + Number.EPSILON * Math.abs(scaled))
  return (value < 0 ? -corrected : corrected) / factor
}

export function num(value: unknown, locale: Locale, places = 0): string {
  if (value === null || value === undefined || value === '') return DASH
  const n = typeof value === 'number' ? value : Number(value)
  if (!Number.isFinite(n)) return String(value)
  const fixed = roundHalfUp(n, places).toFixed(places)
  const [whole, fraction] = fixed.split('.')
  const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, '\u0000')
  let text = grouped.split('\u0000').join(locale.thousand_sep)
  if (fraction) text = `${text}${locale.decimal_sep}${fraction}`
  return digits(text, locale)
}

export function money(value: unknown, currency: Currency | undefined,
                      locale: Locale, places?: number): string {
  if (value === null || value === undefined || value === '') return DASH
  if (!currency) return num(value, locale, places ?? 2)
  const dp = places ?? currency.display_decimals
  const amount = num(value, locale, dp)
  const symbol = locale.direction === 'rtl'
    ? (currency.symbol_ar || currency.symbol)
    : currency.symbol
  return currency.symbol_position === 'before'
    ? `${symbol} ${amount}`
    : `${amount} ${symbol}`
}

function parseDate(value: unknown): Date | null {
  if (!value) return null
  if (value instanceof Date) return value
  const text = String(value)
  // A date without a time is midnight in the viewer's zone if parsed by the
  // browser, which can show the previous day. Parsed by parts, it cannot.
  const plain = /^(\d{4})-(\d{2})-(\d{2})$/.exec(text)
  if (plain) {
    return new Date(Number(plain[1]), Number(plain[2]) - 1, Number(plain[3]))
  }
  const parsed = new Date(text)
  return Number.isNaN(parsed.getTime()) ? null : parsed
}

export function date(value: unknown, locale: Locale): string {
  const d = parseDate(value)
  if (!d) return value ? String(value) : DASH
  const months = locale.direction === 'rtl' ? MONTHS_AR : MONTHS
  const day = String(d.getDate()).padStart(2, '0')
  return digits(`${day} ${months[d.getMonth()]} ${d.getFullYear()}`, locale)
}

export function datetime(value: unknown, locale: Locale): string {
  const d = parseDate(value)
  if (!d) return value ? String(value) : DASH
  const hh = String(d.getHours()).padStart(2, '0')
  const mm = String(d.getMinutes()).padStart(2, '0')
  return `${date(d, locale)} ${digits(`${hh}:${mm}`, locale)}`
}

/** Minutes and hours read as duration, not as a clock time. */
export function hours(value: unknown, locale: Locale): string {
  if (value === null || value === undefined) return DASH
  return `${num(value, locale, 1)} h`
}

/**
 * A cell the server already formatted, with the kinds that need structure
 * rather than text identified. The server's text is used as it stands: it
 * carries the redaction, so reformatting from the raw value would undo a mask.
 */
export const NUMERIC_KINDS = new Set([
  'money', 'int', 'pct', 'rate', 'ms', 'bps', 'score', 'num2', 'rate_raw',
  'conf', 'months', 'mins', 'secs', 'hours', 'bytes',
])

/** Turn a label stored as a code into prose, the way the Jinja `label` filter
 *  does, so the same value reads the same in both tiers. */
export function label(value: unknown): string {
  if (value === null || value === undefined || value === '') return DASH
  const text = String(value).replace(/_/g, ' ')
  return text.charAt(0).toUpperCase() + text.slice(1)
}

/** Pick the Arabic name when the locale reads right to left, and fall back
 *  rather than blanking: a row with no Arabic still shows its English. */
export function nameOf(row: Record<string, unknown> | null | undefined,
                       locale: Locale, base = 'name'): string {
  if (!row) return ''
  const en = row[`${base}_en`] as string | undefined
  const ar = row[`${base}_ar`] as string | undefined
  return (locale.direction === 'rtl' ? (ar || en) : (en || ar)) || ''
}
